import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-neon/30 rounded-full overflow-hidden" data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          className={`font-head font-bold text-xs tracking-[0.15em] uppercase px-3 py-1.5 transition-colors duration-300 ${
            lang === l ? "bg-neon text-ink" : "text-fog hover:text-foreground"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  const LINKS = [
    { to: "/", id: "home", label: t("nav.home") },
    { to: "/menu", id: "menu", label: t("nav.menu") },
    { to: "/chi-siamo", id: "chi-siamo", label: t("nav.about") },
    { to: "/gallery", id: "gallery", label: t("nav.gallery") },
    { to: "/prenota", id: "prenota", label: t("nav.book") },
    { to: "/contatti", id: "contatti", label: t("nav.contacts") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-ink/90 backdrop-blur-xl border-neon/10" : "bg-ink/40 backdrop-blur-md border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between" data-testid="main-nav">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img src="/assets/bella-script.png" alt="Bella" className="h-10 w-auto transition-transform duration-500 group-hover:-rotate-3" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `font-head text-sm font-semibold tracking-[0.18em] uppercase transition-colors duration-300 hover:text-neon ${
                  isActive ? "text-neon" : "text-fog"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <LangToggle />
          <Link
            to="/prenota"
            data-testid="nav-cta-prenota"
            className="bg-neon text-ink font-head font-bold text-sm tracking-[0.15em] uppercase px-6 py-3 rounded-full transition-transform duration-300 hover:scale-105 hover:-rotate-1"
          >
            {t("nav.bookCta")}
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LangToggle />
          <button
            className="text-foreground p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden bg-ink/95 backdrop-blur-xl border-t border-neon/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `font-head text-lg font-semibold tracking-[0.18em] uppercase ${isActive ? "text-neon" : "text-fog"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/prenota"
                data-testid="nav-mobile-cta-prenota"
                className="mt-2 bg-neon text-ink text-center font-head font-bold tracking-[0.15em] uppercase px-6 py-4 rounded-full"
              >
                {t("nav.bookCta")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
